/* eslint-disable import/no-cycle */
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import './styles.css';

import { useTour } from '@reactour/tour';
import { Sidebar, Header } from '@components';
import { fakeAuthProvider } from '@services/auth';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import {
  verificaTourBoasVindas,
  insereRegistroBoasVindas,
} from '../../store/tourBoasVindas/thunks';
import { marcarTourFechado } from '../../store/tourBoasVindas/slice';
import { resetStates, getDadosPessoais } from '../../store/user/thunks';
import { LOGIN } from '../../common/utils/paths';
import { APIAutenticacao } from '../../common/services/autenticacao';
import { PendenciaModal } from '../Pendencia';
import { restoreSession } from '../../store/user/slice';

const AuthContext = React.createContext(null);

export const AuthProvider = ({ children }) => {
  const matriculaLS = localStorage.getItem('Matricula');
  const [user, setUser] = useState(matriculaLS);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const signin = useCallback((newUser, callback) => {
    return fakeAuthProvider.signin(() => {
      if (!isMountedRef.current) {
        return;
      }

      setUser(newUser);
      if (typeof callback === 'function') {
        callback();
      }
    });
  }, []);

  const signout = useCallback(() => {
    return fakeAuthProvider.signout(() => {
      localStorage.removeItem('Token');
      localStorage.removeItem('Plano');
      localStorage.removeItem('Id');
      localStorage.removeItem('Matricula');
      localStorage.removeItem('auth');
      localStorage.removeItem('nivelsessao');

      setUser(null);

      dispatch(resetStates());

      navigate(LOGIN);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const value = useMemo(
    () => ({
      user,
      signin,
      signout,
    }),
    [user, signin, signout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return React.useContext(AuthContext);
};

export const RequireAuth = ({ children, modalPendencias = true }) => {
  const auth = useAuth();
  const dispatch = useDispatch();
  const reduxMatricula = useSelector((state) => state.user.data.matricula);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [isValidating, setIsValidating] = useState(true);

  useEffect(() => {
    let isCancelled = false;

    const validarSessao = async () => {
      const matricula = localStorage.getItem('Matricula') || '';
      const token = localStorage.getItem('Token') || '';
      const id = localStorage.getItem('Id') || '';
      const plano = localStorage.getItem('Plano') || '';

      const camposFaltando = !matricula || !token || !id;

      if (camposFaltando) {
        if (!isCancelled) {
          setShouldRedirect(true);
          auth.signout();
        }
        return;
      }

      const sessaoValida = await APIAutenticacao.validaSessao(matricula, token);

      if (isCancelled) return;

      if (!sessaoValida) {
        setShouldRedirect(true);
        auth.signout();
        return;
      }

      if (!reduxMatricula) {
        dispatch(restoreSession({ matricula, token, plano }));
        const response = await dispatch(getDadosPessoais());

        if (getDadosPessoais.rejected.match(response)) {
          if (!isCancelled) {
            toast.error('Erro ao recarregar seus dados. Faça login novamente.');
            setShouldRedirect(true);
            auth.signout();
          }
          return;
        }
      }

      if (!isCancelled) {
        setIsValidating(false);
      }
    };

    validarSessao();

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (shouldRedirect) {
    return <Navigate to={LOGIN} />;
  }

  if (isValidating) {
    return null;
  }

  return (
    <>
      {children}
      {modalPendencias && <PendenciaModal />}
    </>
  );
};

export const Shell = (props) => {
  const { children } = props;
  const auth = useAuth();
  const location = useLocation();
  const dispatch = useDispatch();

  const users = useSelector((state) => state.user);
  const tourBoasVindas = useSelector((state) => state.tourBoasVindas);
  const pendencias = useSelector((state) => state.user.pendencias);
  const { setIsOpen, isOpen, currentStep } = useTour();
  const tourJaAbertoRef = useRef(false);

  useEffect(() => {
    const handleLogout = () => {
      auth.signout();

      toast.error('Sessão expirada, faça login novamente');
    };

    window.addEventListener('logout', handleLogout, { once: true });
  }, [auth]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  const atualizaTour = useCallback(() => {
    if (users.data.matricula >= '0' && tourBoasVindas.exibir) {
      dispatch(insereRegistroBoasVindas(currentStep + 1));
    }
  }, [dispatch, users.data.matricula, currentStep, tourBoasVindas.exibir]);

  const verificaStatusTour = useCallback(() => {
    if (
      users.data.matricula >= '0' &&
      localStorage.getItem('Matricula') !== null
    ) {
      dispatch(verificaTourBoasVindas());
    }
  }, [users.data.matricula, dispatch]);

  useEffect(() => {
    const hasPendencia = Object.values(pendencias || {}).some(
      (pendencia) => pendencia === 1
    );

    const devePrecisaExibir =
      tourBoasVindas.exibir &&
      users.data.matricula >= '0' &&
      !hasPendencia &&
      !tourJaAbertoRef.current;

    if (devePrecisaExibir) {
      setIsOpen(true);
      tourJaAbertoRef.current = true;
    } else if (!tourBoasVindas.exibir && isOpen) {
      setIsOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourBoasVindas.exibir, users.data.matricula, pendencias]);

  useEffect(() => {
    if (!isOpen && tourJaAbertoRef.current && tourBoasVindas.exibir) {
      dispatch(marcarTourFechado());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    verificaStatusTour();
  }, [verificaStatusTour]);

  useEffect(() => {
    atualizaTour();
  }, [atualizaTour]);

  return (
    <div className="d-flex">
      <Sidebar />
      <div className="container shell">
        <Header />
        {children}
      </div>
    </div>
  );
};
